import { FC, useEffect } from 'react';
import styled from 'styled-components';
import { useLocation } from 'react-router-dom';
import { useQueryClient } from 'react-query';
import { useTranslation } from 'react-i18next';
import {
  FaCheckDouble,
  FaFileSignature,
  FaTable,
  FaCalendarAlt,
  FaDownload,
  FaEye,
  FaEyeSlash,
  FaSuitcase,
} from 'react-icons/fa';
import API from 'core/Api';
import { createErrorFromResponse } from 'core/Error';

import { useActiveClientContext } from 'store/active-client';
import { useModal } from 'store/modal';
import { useSelectableTableContext } from 'store/selectable-table';
import { usePermissionsContext } from 'store/permissions';
import { PermissionKeys } from 'store/types';

import { getUserInfo } from 'utils/auth';
import { getSubdomain } from 'utils/helpers/routing';
import { buildClientEntity, buildClientUrl } from 'utils/helpers/client';
import { requestMultipleDownload, downloadMultipleFiles, extractErrorMessage } from 'utils/helpers/archives';

import MainLayout from 'components/main-layout/MainLayout';
import TabNavigation from 'components/TabNavigation';
import LayoutContent from 'components/main-layout/LayoutContent';
import Button from 'components/Button';
import NotAuthorizedScreen from 'routes/not-authorized';
import TooltipWrapper from 'components/TooltipWrapper';
import { multiSignDocAsEmployer } from '../documents/helpers';

interface IProps {
  isLoading?: boolean;
  error?: unknown;
  canMultiSign?: boolean;
  customSelectableProps?: string[];
  setCustomSelectableRowProps?: (val: string[]) => void;
  setMultiActionActive?: (active: boolean) => void;
  children: React.ReactNode;
  leavesCompleteStatus?: boolean;
  showViewToggle?: boolean;
  isGridView?: boolean;
  onToggleView?: () => void;
  expandEvents?: boolean;
  onToggleExpand?: () => void;
  downloadOnly?: boolean;
  isWorkTripsModuleActive?: boolean | null;
  showWorkTrips?: boolean;
  toggleWorkTrips?: () => void;
}

const InnerWrapper: FC<IProps> = ({
  isLoading,
  children,
  error,
  canMultiSign,
  setCustomSelectableRowProps,
  setMultiActionActive,
  customSelectableProps,
  leavesCompleteStatus = false,
  showViewToggle = false,
  isGridView = false,
  onToggleView,
  expandEvents = false,
  onToggleExpand,
  downloadOnly = false,
  isWorkTripsModuleActive,
  showWorkTrips = true,
  toggleWorkTrips,
}) => {
  const { t } = useTranslation();
  const location = useLocation();
  const { client } = useActiveClientContext();
  const { showModal, hideModal } = useModal();
  const { showSelectColumn, setShowSelectColumn, resetSelectable, selectedRowsOnAction, selectAllRows } =
    useSelectableTableContext();
  const { checkPermission } = usePermissionsContext();
  const queryClient = useQueryClient();
  const isOperator = getSubdomain() === 'operators';
  const isLeaveModuleActive = getUserInfo().featureLeavesIsActive;

  const refreshDocuments = () => {
    const { pathname } = location;
    const currentTab = tabs.find(tab => buildClientUrl(tab.to!, client) === pathname);
    if (currentTab) {
      queryClient.resetQueries(currentTab.entityName);
    }
  };

  useEffect(() => {
    /**
     * If we go between different tabs and then go back
     * to sign tab - reset active table select state.
     */
    resetSelectable();
    if (setMultiActionActive) {
      setMultiActionActive(false);
    }
  }, []);

  const showNoSelectedMsg = () =>
    showModal({
      type: 'error',
      content: t('documents.table.noDocsSelected'),
    });

  const formatValidationMessage = (
    userLeaveGroups: { [key: number]: any[] },
    userDaysData: any,
    isSignFlow: boolean = false
  ) => {
    const validationErrors: { [key: string]: Array<{ user: string; available: number; requested: number }> } = {
      paid: [],
      additional: [],
      servedTime: [],
    };

    let hasValidationError = false;

    Object.keys(userLeaveGroups).forEach(userId => {
      const userLeaves = userLeaveGroups[parseInt(userId, 10)];
      const userDays = userDaysData.items.find((item: any) => item.user_id === parseInt(userId, 10));

      if (!userDays || userDays.has_locked_leaves) {
        return;
      }

      const currentYear = new Date().getFullYear();
      let paidOrdinaryDays = 0;
      let additionalDays = 0;
      let servedTimeDays = 0;

      userLeaves.forEach((leave: any) => {
        const dateFrom = new Date(leave.date_from);
        if (dateFrom.getFullYear() === currentYear) {
          const days = parseFloat(leave.leave_days || 0);
          if (leave.sub_type === 'paid_ordinary') {
            paidOrdinaryDays += days;
          } else if (leave.sub_type === 'additional') {
            additionalDays += days;
          } else if (leave.sub_type === 'paid_served_time') {
            servedTimeDays += days;
          }
        }
      });

      if (paidOrdinaryDays > userDays.paid_leave.current_years) {
        hasValidationError = true;
        validationErrors.paid.push({
          user: userLeaves[0].user_name,
          available: userDays.paid_leave.current_years,
          requested: paidOrdinaryDays,
        });
      }

      if (additionalDays > userDays.leave_days_paid_additional) {
        hasValidationError = true;
        validationErrors.additional.push({
          user: userLeaves[0].user_name,
          available: userDays.leave_days_paid_additional,
          requested: additionalDays,
        });
      }

      if (servedTimeDays > userDays.leave_days_served_time) {
        hasValidationError = true;
        validationErrors.servedTime.push({
          user: userLeaves[0].user_name,
          available: userDays.leave_days_served_time,
          requested: servedTimeDays,
        });
      }
    });

    if (!hasValidationError) {
      return '';
    }

    const actionKey = isSignFlow ? 'sign' : 'approve';
    let message = `${t(`leaves.validation.massAction.${actionKey}.header`)}\n\n`;

    if (validationErrors.paid.length > 0) {
      message += `${t('leaves.validation.massAction.paidType')}:\n`;
      validationErrors.paid.forEach(validationError => {
        message += `${t('leaves.validation.massAction.userFormat', {
          user: validationError.user,
          available: validationError.available,
          requested: validationError.requested,
        })}\n`;
      });
      message += '\n';
    }

    if (validationErrors.servedTime.length > 0) {
      message += `${t('leaves.validation.massAction.servedTimeType')}:\n`;
      validationErrors.servedTime.forEach(validationError => {
        message += `${t('leaves.validation.massAction.userFormat', {
          user: validationError.user,
          available: validationError.available,
          requested: validationError.requested,
        })}\n`;
      });
      message += '\n';
    }

    if (validationErrors.additional.length > 0) {
      message += `${t('leaves.validation.massAction.additionalType')}:\n`;
      validationErrors.additional.forEach(validationError => {
        message += `${t('leaves.validation.massAction.userFormat', {
          user: validationError.user,
          available: validationError.available,
          requested: validationError.requested,
        })}\n`;
      });
    }

    return message.trim();
  };

  const multiApprove = (docIds: number[], validationMessage?: string) => {
    return API.post('/leaves/approve-multiple', {
      leave_ids: docIds,
    })
      .then(res => {
        const { message } = res.data;
        let successContent = '';

        if (message) {
          const { type, body } = message;
          if (type === 'multi_approve') {
            successContent = body;
          }
        }

        // Show mass action message first, then validation warning
        if (validationMessage && successContent) {
          successContent = `${successContent}\n\n${validationMessage}`;
        } else if (validationMessage) {
          successContent = validationMessage;
        }

        if (successContent || message) {
          showModal({
            type: 'success',
            content: successContent || message?.body || t('common.success'),
          });
        }
        queryClient.resetQueries(buildClientEntity('leaves', client));
      })
      .catch(err =>
        showModal({
          type: 'error',
          content: createErrorFromResponse(err).message,
        })
      )
      .finally(() => resetSelectable());
  };

  const multiSign = async (rowIds: number[], validationMessage?: string) => {
    const getDocumentHoldersForLeave = async (leaveId: number) => {
      const res = await API.get(`leaves/${leaveId}/documents-holders`);
      return [res.data.request_document_holder_id, res.data.order_document_holder_id];
    };

    try {
      const allDocumentHolders = await Promise.all(rowIds.map(getDocumentHoldersForLeave));
      const allIds = allDocumentHolders.flat();

      showModal({
        type: 'loading',
        heading: t('leaves.eSign'),
        content: t('leaves.eSignSubtitle'),
      });

      const customShowModal = (modalConfig: any) => {
        if (modalConfig.type === 'success' && validationMessage) {
          // Show mass action message first, then validation warning
          const originalContent = modalConfig.content || '';
          const enhancedConfig = {
            ...modalConfig,
            content: `${originalContent}\n\n${validationMessage}`,
          };
          showModal(enhancedConfig);
        } else {
          showModal(modalConfig);
        }
      };

      await multiSignDocAsEmployer(
        allIds,
        customShowModal,
        () => queryClient.resetQueries(buildClientEntity('documents-for-sign', client)),
        '',
        t
      ).then(() => {
        queryClient.resetQueries(buildClientEntity('leaves', client));
        resetSelectable();
      });
    } catch (err) {
      resetSelectable();
      showModal({
        type: 'error',
        content: createErrorFromResponse(err).message,
      });
    }
  };

  const confirmApproveFlow = (rowIds: number[]) => {
    if (rowIds.length) {
      API.get('leaves/user_days')
        .then(res => {
          const userDaysData = res.data;

          return API.get('leaves/search?limit=1000&offset=0').then(leavesRes => {
            const allLeaves = leavesRes.data.items || [];

            const selectedLeaves = allLeaves.filter((leave: any) => {
              return rowIds.includes(parseInt(leave.id, 10));
            });

            const userLeaveGroups: { [key: number]: any[] } = {};
            selectedLeaves.forEach((leave: any) => {
              if (!userLeaveGroups[leave.user_id]) {
                userLeaveGroups[leave.user_id] = [];
              }
              userLeaveGroups[leave.user_id].push(leave);
            });

            const validationWarning = formatValidationMessage(userLeaveGroups, userDaysData, false);

            showModal({
              type: 'confirm',
              content: t('leaves.confirmLeaveAware'),
              onCancel: () => {
                resetSelectable();
                if (setMultiActionActive) {
                  setMultiActionActive(false);
                }
              },
              onConfirm: () =>
                showModal({
                  type: 'confirm',
                  content: t('leaves.confirmApproveLeaveMsg'),
                  onCancel: () => {
                    resetSelectable();
                    if (setMultiActionActive) {
                      setMultiActionActive(false);
                    }
                  },
                  confirmButton: (
                    <Button
                      className="confirm-btn"
                      btnType="primary"
                      onClick={() => {
                        hideModal();
                        multiApprove(rowIds, validationWarning);
                        if (setMultiActionActive) {
                          setMultiActionActive(false);
                        }
                      }}
                    >
                      {t('common.confirm')}
                    </Button>
                  ),
                }),
            });
          });
        })
        .catch(err => {
          showModal({
            type: 'error',
            content: t('leaves.validation.errorFetchingData'),
          });
        });
    } else {
      showNoSelectedMsg();
    }
  };

  const confirmSignFlow = (rowIds: number[]) => {
    if (rowIds.length) {
      API.get('leaves/user_days')
        .then(res => {
          const userDaysData = res.data;

          return API.get('leaves/search?limit=1000&offset=0').then(leavesRes => {
            const allLeaves = leavesRes.data.items || [];

            const selectedLeaves = allLeaves.filter((leave: any) => {
              return rowIds.includes(parseInt(leave.id, 10));
            });

            const userLeaveGroups: { [key: number]: any[] } = {};
            selectedLeaves.forEach((leave: any) => {
              if (!userLeaveGroups[leave.user_id]) {
                userLeaveGroups[leave.user_id] = [];
              }
              userLeaveGroups[leave.user_id].push(leave);
            });

            const validationWarning = formatValidationMessage(userLeaveGroups, userDaysData, true);

            showModal({
              type: 'confirm',
              content: t('leaves.confirmLeaveAware'),
              onCancel: () => {
                resetSelectable();
                if (setMultiActionActive) {
                  setMultiActionActive(false);
                }
              },
              onConfirm: () =>
                showModal({
                  type: 'confirm',
                  content: t('leaves.confirmSignLeaveMsg'),
                  onCancel: () => {
                    resetSelectable();
                    if (setMultiActionActive) {
                      setMultiActionActive(false);
                    }
                  },
                  confirmButton: (
                    <Button
                      className="confirm-btn"
                      btnType="primary"
                      onClick={() => {
                        hideModal();
                        multiSign(rowIds, validationWarning);
                        if (setMultiActionActive) {
                          setMultiActionActive(false);
                        }
                      }}
                    >
                      {t('common.confirm')}
                    </Button>
                  ),
                }),
            });
          });
        })
        .catch(err => {
          showModal({
            type: 'error',
            content: t('leaves.validation.errorFetchingData'),
          });
        });
    } else {
      showNoSelectedMsg();
    }
  };

  const confirmBulkDownloadFlow = async (rowIds: number[]) => {
    if (rowIds.length === 0) {
      showNoSelectedMsg();
      return;
    }

    try {
      const leaveRequests = rowIds.map(id => API.get(`leaves/${id}`));
      const leavesResponses = await Promise.all(leaveRequests);
      const selectedLeaves = leavesResponses.map(res => res.data);

      const documentIds: number[] = [];

      selectedLeaves.forEach((leave: any) => {
        if (leave.request_document_id) {
          documentIds.push(leave.request_document_id);
        }
        if (leave.order_document_id) {
          documentIds.push(leave.order_document_id);
        }
        if (leave.reject_document_id) {
          documentIds.push(leave.reject_document_id);
        }
        if (leave.reject_request_document_id) {
          documentIds.push(leave.reject_request_document_id);
        }
      });

      showModal({
        type: 'loading',
        heading: t('documents.bulkDownload.preparing'),
        content: t('documents.bulkDownload.pleaseWait'),
      });

      const response = await requestMultipleDownload(documentIds, 'leaves-tab');

      if (response.type === 'immediate-download') {
        hideModal();
        showModal({
          type: 'success',
          content: t('documents.bulkDownload.immediateDownloadMessage'),
        });

        downloadMultipleFiles(response.links);

        resetSelectable();
        if (setMultiActionActive) {
          setMultiActionActive(false);
        }
      } else {
        hideModal();
        showModal({
          type: 'success',
          heading: t('documents.bulkDownload.archiveGenerating'),
          content: t('documents.bulkDownload.archiveMessage'),
        });

        resetSelectable();
        if (setMultiActionActive) {
          setMultiActionActive(false);
        }
      }
    } catch (err: any) {
      hideModal();
      const errorMessage = extractErrorMessage(err, t('documents.bulkDownload.error'));
      showModal({
        type: 'error',
        content: errorMessage,
      });
    }
  };

  const tabs = [
    {
      to: '/leaves',
      label: t('leaves.employer.leavesTableView'),
      entityName: buildClientEntity('leaves', client),
      isExact: true,
    },
    {
      to: '/leaves/completed',
      label: t('leaves.employer.leavesTableCompletedView'),
      entityName: buildClientEntity(`leaves-${leavesCompleteStatus}`, client),
      isExact: true,
    },
    {
      to: '/leaves/calendar',
      label: t('leaves.employer.calendarView'),
      entityName: buildClientEntity('leaves', client),
      isExact: true,
    },
    {
      to: '/leaves/statistics',
      label: t('leaves.employer.statisticsView'),
      entityName: buildClientEntity('leaves', client),
      isExact: true,
    },
    {
      onRefresh: refreshDocuments,
    },
  ];

  if (isLeaveModuleActive || (isOperator && client?.leaves)) {
    return (
      <MainLayout title={t('leaves.title')}>
        <div className="d-flex flex-column flex-md-row justify-content-md-between align-items-md-center">
          <div className="d-flex flex-column flex-md-row align-items-md-center">
            <TabNavigation tabs={tabs} />
            {showViewToggle && onToggleView && (
              <ButtonContainer>
                {!isGridView && onToggleExpand && (
                  <StyledTooltipWrapper
                    id="tooltip-expand-events"
                    overlay={expandEvents ? t('leaves.collapse') : t('leaves.expand')}
                  >
                    <ExpandButton
                      className="mb-3 mr-2"
                      btnType="secondary"
                      onClick={onToggleExpand}
                    >
                      {expandEvents ? <FaEyeSlash size="20" /> : <FaEye size="20" />}
                      <span className="ml-2">{t('leaves.allLeaves')}</span>
                    </ExpandButton>
                  </StyledTooltipWrapper>
                )}
                <StyledTooltipWrapper
                  id="tooltip-toggle-view"
                  overlay={
                    isGridView
                      ? `${t('leaves.switchTo')} ${t('leaves.showCalendarView')}`
                      : `${t('leaves.switchTo')} ${t('leaves.showGridView')}`
                  }
                >
                  <ToggleButton
                    className="mb-3 mr-2"
                    btnType="secondary"
                    onClick={onToggleView}
                  >
                    {!isGridView ? (
                      <>
                        <FaCalendarAlt
                          size="16"
                          className="mr-2"
                        />
                        {t('leaves.showCalendarView')}
                      </>
                    ) : (
                      <>
                        <FaTable
                          size="16"
                          className="mr-2"
                        />
                        {t('leaves.showGridView')}
                      </>
                    )}
                  </ToggleButton>
                </StyledTooltipWrapper>
              </ButtonContainer>
            )}
            {isWorkTripsModuleActive && (
              <StyledTooltipWrapper
                id="tooltip-show-work-trips"
                overlay={showWorkTrips ? t('leaves.hideWorkTrips') : t('leaves.showWorkTrips')}
              >
                <ToggleButton
                  className="mb-3"
                  btnType="light"
                  onClick={toggleWorkTrips}
                >
                  <IconWrapper isSelected={!showWorkTrips}>
                    <FaSuitcase
                      size="16"
                      className="mr-2"
                    />
                  </IconWrapper>
                  {t('leaves.workTrips')}
                </ToggleButton>
              </StyledTooltipWrapper>
            )}
          </div>
          {canMultiSign && !showSelectColumn && (
            <MultiActionWrapper>
              <StyledH5>{t('leaves.table.multiActions')}:</StyledH5>

              <ButtonsWrapper>
                {!downloadOnly && checkPermission(PermissionKeys.CAN_MULTI_APPROVE) && (
                  <Button
                    className="ml-2 mb-3"
                    onClick={() => {
                      if (setCustomSelectableRowProps) setCustomSelectableRowProps([PermissionKeys.CAN_MULTI_APPROVE]);
                      setShowSelectColumn(true);
                      if (setMultiActionActive) {
                        setMultiActionActive(true);
                      }
                    }}
                  >
                    <FaCheckDouble className="mr-2" />
                    {t('leaves.table.approval')}
                  </Button>
                )}
                {!downloadOnly && checkPermission(PermissionKeys.CAN_MULTI_SIGN) && (
                  <Button
                    className="ml-2 mb-3"
                    btnType="info"
                    onClick={() => {
                      if (setCustomSelectableRowProps) setCustomSelectableRowProps([PermissionKeys.CAN_MULTI_SIGN]);
                      setShowSelectColumn(true);
                      if (setMultiActionActive) {
                        setMultiActionActive(true);
                      }
                    }}
                  >
                    <FaFileSignature className="mr-2" />
                    {t('leaves.table.signing')}
                  </Button>
                )}
                <Button
                  className="ml-2 mb-3"
                  btnType="secondary"
                  onClick={() => {
                    if (setCustomSelectableRowProps) setCustomSelectableRowProps(['CAN_MULTI_DOWNLOAD']);
                    setShowSelectColumn(true);
                    if (setMultiActionActive) {
                      setMultiActionActive(true);
                    }
                  }}
                >
                  <TooltipWrapper
                    id="tooltip-mass-download"
                    style={{ width: '100%' }}
                    overlay={t('documents.bulkDownload.tooltip')}
                  >
                    <FaDownload className="mr-2" />
                    {t('documents.table.download')}
                  </TooltipWrapper>
                </Button>
              </ButtonsWrapper>
            </MultiActionWrapper>
          )}
          {canMultiSign && showSelectColumn && (
            <div className="mb-3">
              {!downloadOnly && customSelectableProps?.includes(PermissionKeys.CAN_MULTI_APPROVE) && (
                <Button
                  className="ml-2"
                  btnType="primary"
                  onClick={() => selectedRowsOnAction(confirmApproveFlow)}
                  preventDebounce
                >
                  <FaCheckDouble
                    size={14}
                    className="mr-2"
                  />
                  {t('leaves.table.approve')}
                </Button>
              )}
              {!downloadOnly && customSelectableProps?.includes(PermissionKeys.CAN_MULTI_SIGN) && (
                <Button
                  className="ml-2"
                  btnType="info"
                  onClick={() => selectedRowsOnAction(confirmSignFlow)}
                  preventDebounce
                >
                  <FaFileSignature
                    size={14}
                    className="mr-2"
                  />
                  {t('leaves.table.sign')}
                </Button>
              )}
              {customSelectableProps?.includes('CAN_MULTI_DOWNLOAD') && (
                <Button
                  className="ml-2"
                  btnType="secondary"
                  onClick={() => selectedRowsOnAction(confirmBulkDownloadFlow)}
                  preventDebounce
                >
                  <FaDownload
                    size={14}
                    className="mr-2"
                  />
                  {t('documents.table.download')}
                </Button>
              )}
              <Button
                className="ml-2"
                btnType="light"
                outlined
                onClick={() => {
                  resetSelectable();
                  if (setMultiActionActive) {
                    setMultiActionActive(false);
                  }
                }}
              >
                {t('common.cancel')}
              </Button>
            </div>
          )}
        </div>
        <LayoutContent
          isLoading={isLoading}
          error={error}
        >
          {children}
        </LayoutContent>
      </MainLayout>
    );
  }

  return <NotAuthorizedScreen />;
};

export default InnerWrapper;

const MultiActionWrapper = styled.div`
  display: block;
`;

const ButtonsWrapper = styled.div`
  display: flex;
  flex-wrap: nowrap;
  justify-content: flex-end;
  gap: 0.5rem;

  button {
    margin-left: 0 !important;
    margin-bottom: 0 !important;
    white-space: nowrap;
  }

  @media ${props => props.theme.breakpoints.md} {
    flex-wrap: wrap;

    button {
      flex: 1 1 auto;
      min-width: 150px;
    }
  }

  @media ${props => props.theme.breakpoints.sm} {
    button {
      flex: 1 1 100%;
    }
  }
`;

const StyledH5 = styled.h5`
  text-align: right;
  padding-right: 5px;
  position: relative;
  margin: 0 0 0.5rem 0;
  white-space: nowrap;

  @media ${props => props.theme.breakpoints.md} {
    text-align: center;
  }
`;

const ButtonContainer = styled.div`
  margin-left: auto;

  @media ${props => props.theme.breakpoints.sm} {
    margin-left: 0;
    width: 100%;
    display: flex;
    flex-direction: column-reverse;
  }
`;

const ToggleButton = styled(Button)`
  display: inline-flex;

  @media ${props => props.theme.breakpoints.sm} {
    min-width: 100% !important;
    display: inline;
  }
`;

const ExpandButton = styled(Button)`
  svg {
    filter: brightness(0) invert(1);
  }

  @media ${props => props.theme.breakpoints.sm} {
    min-width: 100% !important;
  }
`;

const StyledTooltipWrapper = styled(TooltipWrapper)`
  width: 100% !important;
  display: block !important;
`;

interface IconWrapperProps {
  isSelected: boolean;
}

export const IconWrapper = styled.div<IconWrapperProps>`
  display: inline-flex;
  position: relative;
  align-items: center;
  justify-content: center;

  &::after {
    content: '${props => (props.isSelected ? '' : '/')}';
    position: absolute;
    color: red;
    font-size: 28px;
    transform: rotate(30deg);
    margin-right: 8px;
  }
`;
